import _ from 'underscore';
import { $current } from '@bingads-webui-react/scope-service';

function getSearch(scope, params = {}) {
  const mergedScope = scope.getMerged(_.defaults(
    params,
    {
      uid: $current,
      cid: $current,
    },
    scope.values.aid ? { aid: $current } : undefined
  ));
  return `?${scope.stringify(mergedScope)}`;
}

const recommendationSubRoute = {
  BroadMatchKeywordOpportunity: 'Keywords',
  BroadMatchModifierKeywordOpportunity: 'Keywords',
  CampaignContextKeywordOpportunity: 'Keywords',
  GoogleImportOpportunity: 'Keywords',
  GoogleImportScheduledOpportunity: 'Keywords',
  CalloutOpportunity: 'Ads',
  SiteLinkOpportunity: 'Ads',
  AdSchedulingBidAdjustmentOpportunity: 'Bids',
  CompetitiveBidOpportunity: 'Bids',
  BudgetOpportunity: 'Bids',
  FirstPageBidOpportunity: 'Bids',
  KeywordBidAdjustmentOpportunity: 'Bids',
  LocationBidAdjustmentOpportunity: 'Bids',
  MainLine1BidOpportunity: 'Bids',
  MainLineBidOpportunity: 'Bids',
  RemarketingBidAdjustmentOpportunity: 'Bids',
};

function getRecommendationSubRoutes({ type, category }) {
  if (!_.isEmpty(category)) {
    return category;
  }

  return _.result(recommendationSubRoute, type, 'All');
}

export const routerNameMapper = {
  accounts_performance: (scope, params = {}) => ({
    route: 'accounts/performance',
    search: getSearch(scope, { accountMode: params.accountMode }),
  }),
  accounts_management: scope => ({
    route: 'accounts/management',
    search: getSearch(scope),
  }),
  account_$summary: (scope, params = {}) => ({
    search: getSearch(scope, { aid: params.account_id }),
  }),
  bing_merchant_center: (scope, params = {}) => ({
    route: 'merchantcenter',
    search: getSearch(scope, params),
  }),
  merchant_stores: scope => ({
    route: 'merchantcenter/stores',
    search: getSearch(scope),
  }),
  merchant_imports: (scope, params = {}) => ({
    route: 'merchantcenter/import',
    search: getSearch(scope, { storeId: params.storeId }),
  }),
  merchant_import_steps: (scope, params = {}) => ({
    route: 'merchantcenter/import-steps',
    search: getSearch(scope, { storeId: params.storeId, editMode: params.editMode || false }),
  }),
  merchant_import_result: (scope, params = {}) => ({
    route: 'merchantcenter/import-result',
    search: getSearch(scope, { storeId: params.storeId }),
  }),
  merchant_store_summary: (scope, params = {}) => ({
    route: 'merchantcenter/store-summary',
    search: getSearch(scope, { storeId: params.storeId }),
  }),
  merchant_create_store: (scope, params = {}) => ({
    route: 'merchantcenter/create-store',
    search: params.placeholderId === undefined ? getSearch(scope) : getSearch(scope, { placeholderId: params.placeholderId }),
  }),
  merchant_feeds: (scope, params = {}) => ({
    route: 'merchantcenter/feeds',
    search: getSearch(scope, { storeId: params.storeId }),
  }),
  merchant_feed_details: (scope, params = {}) => ({
    route: 'merchantcenter/feed-details',
    search: getSearch(scope, { storeId: params.storeId, feedId: params.feedId }),
  }),
  merchant_settings_details: (scope, params = {}) => ({
    route: 'merchantcenter/settings/autoupdates',
    search: getSearch(scope, { storeId: params.storeId }),
  }),
  merchant_product_issues: (scope, params = {}) => ({
    route: 'merchantcenter/store-summary/productissues',
    search: getSearch(scope, params),
  }),
  merchant_product_issues_details: (scope, params = {}) => ({
    route: 'merchantcenter/store-summary/productissuedetails',
    search: getSearch(scope, { storeId: params.storeId, issueCode: params.issueCode }),
  }),
  merchant_policy_violation_details: (scope, params = {}) => ({
    route: 'merchantcenter/store-summary/policyviolationdetails',
    search: getSearch(scope, { storeId: params.storeId, issueCode: params.issueCode }),
  }),
  merchant_review_rejections: (scope, params = {}) => ({
    route: 'merchantcenter/store-summary/reviewrejections',
    search: getSearch(scope, { storeId: params.storeId, issueCode: params.issueCode, flagAreaIdCode: params.flagAreaIdCode }),
  }),
  merchant_ftp_settings: (scope, params = {}) => ({
    route: 'merchantcenter/settings/ftp',
    search: getSearch(scope, params),
  }),
  merchant_placeholder_stores: (scope, params = {}) => ({
    route: 'merchantcenter/placeholderStores',
    search: getSearch(scope, { cid: params.cid }),
  }),
  campaign: scope => ({
    route: 'campaigns',
    search: getSearch(scope),
  }),
  conversiongoals: (scope, params = {}) => ({
    route: 'conversiongoals',
    search: getSearch(scope, { filter: params.filter }),
  }),
  adgroup: scope => ({
    route: 'adgroups',
    search: getSearch(scope),
  }),
  campaign_adgroup: (scope, params = {}) => ({
    route: 'adgroups',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  bing_ads_script: scope => ({
    route: 'scripts',
    search: getSearch(scope),
  }),
  bing_ads_script_history: scope => ({
    route: 'scriptshistory',
    search: getSearch(scope),
  }),
  bing_ads_script_edit: (scope, params = {}) => ({
    route: 'scriptsedit',
    search: getSearch(scope, { scriptId: params.script_id }),
  }),
  bing_ads_script_create: scope => ({
    route: 'scriptscreate',
    search: getSearch(scope),
  }),
  bing_ads_script_log_detail: (scope, params = {}) => ({
    route: 'scriptslogs',
    search: getSearch(scope, { logId: params.log_id }),
  }),
  adgroup_ad: (scope, params = {}) => ({
    route: 'ads',
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  single_ad: (scope, params = {}) => ({
    route: 'ad',
    search: getSearch(scope, _.extend(
      { campaignId: params.campaign_id, adGroupId: params.adgroup_id },
      params.ad_id ? { adId: params.ad_id } : null
    )),
  }),
  campaign_edit: (scope, params = {}) => ({
    route: 'settings',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  shared_budget_edit: (scope, params = {}) => ({
    route: 'sharedbudgets',
    search: getSearch(scope, { budgetId: params.budget_id }),
  }),
  portfolio_bid_strategy_performance: (scope, { portfolioId }) => ({
    route: 'portfoliobidstrategy/performance',
    search: getSearch(scope, { portfolioId }),
  }),
  portfolio_bid_strategy_settings: (scope, { portfolioId }) => ({
    route: 'portfoliobidstrategy/settings',
    search: getSearch(scope, { portfolioId }),
  }),
  bulk_edit: scope => ({
    route: 'bulkeditoperation',
    search: getSearch(scope),
  }),
  bulk_rules: scope => ({
    route: 'automatedruleoperation',
    search: getSearch(scope),
  }),
  keyword: scope => ({
    route: 'keywords',
    search: getSearch(scope),
  }),
  campaign_keyword: (scope, params = {}) => ({
    route: 'keywords',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  keyword_search_term: scope => ({
    route: 'searchterms',
    search: getSearch(_.omit(scope, 'selectedKeywords')),
  }),
  keyword_search_term_selected: (scope, params = {}) => ({
    route: 'searchterms/select',
    search: getSearch(scope, { selectedKeywords: params.selected_keywords, campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  campaign_keyword_search_term: (scope, params = {}) => ({
    route: 'searchterms',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  campaign_settings: (scope, params = {}) => ({
    route: params.setting,
    search: getSearch(scope),
  }),
  campaign_edit_settings: (scope, params = {}) => ({
    route: params.setting,
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  campaign_$summary: (scope, params = {}) => ({
    route: 'overview',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  adgroup_$summary: (scope, params = {}) => ({
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  adgroup_id: (scope, params = {}) => ({
    route: 'keywords',
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  campaign_id: (scope, params = {}) => ({
    route: 'adgroups',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  library_keyword_negative: scope => ({
    route: 'negativekeywordlistslibrary',
    search: getSearch(scope),
  }),
  negative_keyword_list: (scope, params = {}) => ({
    route: 'negativekeywordslistsdetails/negativekeywords',
    search: getSearch(scope, { nkl: params.negative_keyword_list_id }),
  }),
  account_entry: (scope, params = {}) => ({
    route: '',
    search: getSearch(scope, {
      uid: params.user_id, cid: params.customer_id, aid: params.account_id,
    }),
  }),
  campaign_create_objective: (scope, params = {}) => ({
    route: 'createcampaign/objective',
    search: getSearch(scope, {
      selectedCampaignObjectiveKey: params.selected_item,
    }),
  }),
  label_manage: (scope, params = {}) => ({
    route: 'labels',
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  recommendation: scope => ({
    route: 'recommendations/All',
    search: getSearch(scope),
  }),
  competition: scope => ({
    route: 'competitionauction',
    search: getSearch(scope),
  }),
  campaign_competition: (scope, params) => ({
    route: 'competitionauction',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  adgroup_competition: (scope, params) => ({
    route: 'competitionauction',
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  campaigns_competition: scope => ({
    route: 'campaignsauction',
    search: getSearch(scope),
  }),
  adgroups_competition: scope => ({
    route: 'adgroupsauction',
    search: getSearch(scope),
  }),
  adgroups_campaign_competition: (scope, params) => ({
    route: 'adgroupsauction',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  keywords_competition: scope => ({
    route: 'keywordsauction',
    search: getSearch(scope),
  }),
  keywords_campaign_competition: (scope, params) => ({
    route: 'keywordsauction',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  keywords_adgroup_competition: (scope, params) => ({
    route: 'keywordsauction',
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  uet_tags: scope => ({
    route: 'uettag',
    search: getSearch(scope),
  }),
  adgroup_ad_assets: (scope, params = {}) => ({
    route: 'assets',
    search: getSearch(scope, {
      campaignId: params.campaign_id, adGroupId: params.adgroup_id, adId: params.ad_id, adType: params.ad_type, campaignType: params.campaign_type, adSubType: params.ad_sub_type,
    }),
  }),
  recommendation_details: (scope, params = {}) => {
    const subroute = getRecommendationSubRoutes(params);

    return {
      route: `recommendations/${subroute}`,
      search: getSearch(scope, _.extend({ recommendationType: params.type })),
    };
  },
  recommendation_history: scope => ({
    route: 'recommendations/history',
    search: getSearch(scope),
  }),
  campaign_recommendation_details: (scope, params = {}) => {
    const campaignId = params.campaign_id;
    const subroute = getRecommendationSubRoutes(params);

    return {
      route: `recommendations/${subroute}`,
      search: getSearch(scope, _.extend(
        { recommendationType: params.type },
        campaignId ? { campaignId } : null
      )),
    };
  },
  adgroup_recommendation_details: (scope, params = {}) => {
    const campaignId = params.campaign_id;
    const adGroupId = params.adgroup_id;
    const subroute = getRecommendationSubRoutes(params);

    return {
      route: `recommendations/${subroute}`,
      search: getSearch(scope, _.extend(
        { recommendationType: params.type },
        campaignId ? { campaignId } : null,
        adGroupId ? { adGroupId } : null
      )),
    };
  },
  mcc_recommendation_details: (scope, params = {}) => {
    const subroute = getRecommendationSubRoutes(params);

    return {
      route: `accounts/recommendations/${subroute}`,
      search: getSearch(scope, _.extend({ recommendationType: params.type })),
    };
  },
  campaign_autotargets: (scope, params = {}) => ({
    route: 'dynamicadtargets/targets',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  adgroup_autotargets: (scope, params = {}) => ({
    route: 'dynamicadtargets/targets',
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  campaign_autotargets_exclusions: (scope, params = {}) => ({
    route: 'dynamicadtargets/negative/campaign',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  adgroup_autotargets_exclusions: (scope, params = {}) => ({
    route: 'dynamicadtargets/negative/adgroup',
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  campaign_autotargets_searchterm: (scope, params = {}) => ({
    route: 'dynamicadtargets/searchterms',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  adgroup_autotargets_searchterm: (scope, params = {}) => ({
    route: 'dynamicadtargets/searchterms',
    search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
  }),
  campaign_keyword_type: (scope, params = {}) => {
    let route = 'keywords';
    if (params.type === 'negative') {
      route = 'negative-keywords';
    }
    return {
      route,
      search: getSearch(scope, { campaignId: params.campaign_id }),
    };
  },
  adgroup_keyword_type: (scope, params = {}) => {
    let route = 'keywords';
    if (params.type === 'negative') {
      route = 'negative-keywords';
    }
    return {
      route,
      search: getSearch(scope, { campaignId: params.campaign_id, adGroupId: params.adgroup_id }),
    };
  },
  experiments: scope => ({
    route: 'experiments',
    search: getSearch(scope),
  }),
  campaign_experiments: (scope, params = {}) => ({
    route: 'experiments',
    search: getSearch(scope, { campaignId: params.campaign_id }),
  }),
  performance_targets: scope => ({
    route: 'performancetargets',
    search: getSearch(scope),
  }),
  performance_targets_overview: (scope, params = {}) => {
    const ptId = params.performance_target_id;
    return {
      route: 'performancetargets/overview',
      search: getSearch(scope, { ptId }),
    };
  },
  performance_targets_instance: (scope, params = {}) => {
    const ptId = params.performance_target_id;
    const instanceId = params.instance_id;
    return {
      route: 'performancetargets/overview',
      search: getSearch(scope, _.extend({ ptId }, instanceId ? { instanceId } : null)),
    };
  },
  brands: scope => ({
    route: 'brand',
    search: getSearch(scope),
  }),
  msstoreads: scope => ({
    route: 'msstoreads',
    search: getSearch(scope),
  }),
  urloptions: scope => ({
    route: 'accountsettings',
    search: getSearch(scope),
  }),
  facebookimport: (scope, params = {}) => ({
    route: 'facebookimport',
    search: getSearch(scope, params),
  }),
  amazonimport: (scope, params = {}) => ({
    route: 'amazonimport',
    search: getSearch(scope, params),
  }),
  googleimport: (scope, params = {}) => ({
    route: 'googleimport',
    search: getSearch(scope, params),
  }),
  pinterestimport: (scope, params = {}) => ({
    route: 'pinterestimport',
    search: getSearch(scope, params),
  }),
  fileimport: (scope, params = {}) => ({
    route: 'fileimport',
    search: getSearch(scope, params),
  }),
  importschedule: (scope, params = {}) => ({
    route: 'importschedule',
    search: getSearch(scope, params),
  }),
  dynamicdata_management: (scope, params = {}) => ({
    route: 'feeds/dynamicdata',
    search: getSearch(scope, params),
  }),
};
